import { randomUniqueId } from "$routes/_lib/random-id";
import { Box, styled, Typography } from "@material-ui/core";
import * as Sentry from "@sentry/react";
import { nanoid } from "nanoid";
import { useState } from "react";
import { useRxCollectionEpocusEncounter } from "../../../../../global-providers/rxdb-provider/collections/epocus-encounter";
import { useRxCollectionImagingStudy } from "../../../../../global-providers/rxdb-provider/collections/imaging-study";
import { useUser } from "../../../../../global-providers/user-provider/provider";
import {
  EPOCUS_BORDER,
  EPOCUS_PRIMARY,
  EPOCUS_TEXT_SECONDARY,
  EPOCUS_WHITE,
  rem,
} from "../../../_lib/epocus-palette";
import { PatientSearchField } from "../../../_lib/patient-link/patient-search-field";
import type { PatientSearchResult } from "../../../_lib/patient-link/use-patient-search";

const Card = styled(Box)({
  backgroundColor: EPOCUS_WHITE,
  border: `1px solid ${EPOCUS_BORDER}`,
  borderRadius: 12,
  padding: 16,
  display: "flex",
  flexDirection: "column",
  gap: 12,
});

const CardLabel = styled(Typography)({
  fontSize: rem(12),
  fontWeight: 700,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  color: EPOCUS_TEXT_SECONDARY,
});

type Props = {
  encounterId: string;
};

export const LinkPatientSection: FC<Props> = ({ encounterId }) => {
  const encounterCollection = useRxCollectionEpocusEncounter();
  const imagingStudyCollection = useRxCollectionImagingStudy();
  const user = useUser();
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const handleSelect = async (patient: PatientSearchResult | null) => {
    if (patient === null || !encounterCollection || !imagingStudyCollection) {
      return;
    }
    setIsSaving(true);
    setError(null);
    try {
      const doc = await encounterCollection.findOne(encounterId).exec();
      if (doc == null) {
        throw new Error(`Encounter ${encounterId} not found`);
      }

      const existingAccession = doc.accessionNumber?.trim() ?? "";
      const accessionNumber =
        existingAccession.length > 0 ? existingAccession : randomUniqueId(16);

      await doc.incrementalPatch({
        elephantPatientId: patient.patientId,
        patientName: patient.displayName,
        ...(existingAccession.length === 0 ? { accessionNumber } : {}),
      });

      const existingStudy = await imagingStudyCollection
        .findOne({ selector: { accessionNumber } })
        .exec();
      if (existingStudy == null) {
        const now = new Date().toISOString();
        await imagingStudyCollection.insert({
          id: nanoid(),
          elephantPatientId: patient.patientId,
          accessionNumber,
          meta: {
            createdAt: now,
            createdByUserId: user.id,
            updatedAt: now,
            updatedByUserId: user.id,
          },
        });
      }
    } catch (err) {
      console.error("Failed to link patient to encounter", {
        encounterId,
        error: err,
      });
      Sentry.captureException(err);
      setError("Failed to link patient. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Card>
      <CardLabel>Link a patient</CardLabel>
      <PatientSearchField
        selectedPatient={null}
        onSelect={handleSelect}
        disabled={isSaving}
      />
      {error && (
        <Typography
          style={{ fontSize: rem(13), color: "#dc2626", fontWeight: 600 }}
        >
          {error}
        </Typography>
      )}
      {isSaving && (
        <Typography
          style={{
            fontSize: rem(13),
            color: EPOCUS_PRIMARY,
            fontWeight: 600,
          }}
        >
          Linking…
        </Typography>
      )}
    </Card>
  );
};
